import { Controller, type UseFormReturn } from 'react-hook-form'

import { DOBInput } from '@/components/ui/DateInput'
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PhoneInput } from '@/components/ui/phoneInput'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { FillFieldKey, useFieldCheck } from '@/models/fillApplication'
import { validatePhone } from '@/utils/phone'
import { MARITAL_STATUS_OPTIONS } from '../../models/user'
import type { OwnerInfoValues } from './useOwnerInfoForm'

interface OwnerInfoFieldsProps {
  form: UseFormReturn<OwnerInfoValues>
  /** Two pairs the fields side by side on wide screens, for pages wider than a dialog. */
  columns?: 1 | 2
  idPrefix?: string
}

export function OwnerInfoFields({ form, columns = 1, idPrefix = 'oi' }: OwnerInfoFieldsProps) {
  const { control } = form
  const id = (name: string) => `${idPrefix}-${name}`
  // Shown at the field while it stands, as the field's own errors are.
  const nameCheck = useFieldCheck(FillFieldKey.OWNER_NAME)
  const emailCheck = useFieldCheck(FillFieldKey.OWNER_EMAIL)
  const dobCheck = useFieldCheck(FillFieldKey.OWNER_DOB)
  const maritalStatusCheck = useFieldCheck(FillFieldKey.OWNER_MARITAL_STATUS)

  const firstName = (
    <Controller
      name="firstName"
      control={control}
      render={({ field, fieldState }) => (
        <Field>
          <Label htmlFor={id('first-name')}>First name</Label>
          <Input
            {...field}
            id={id('first-name')}
            aria-invalid={!!(fieldState.error || nameCheck) || undefined}
          />
          <FieldError>{fieldState.error?.message ?? nameCheck}</FieldError>
        </Field>
      )}
    />
  )
  const lastName = (
    <Controller
      name="lastName"
      control={control}
      render={({ field, fieldState }) => (
        <Field>
          <Label htmlFor={id('last-name')}>Last name</Label>
          <Input
            {...field}
            id={id('last-name')}
            aria-invalid={!!(fieldState.error || nameCheck) || undefined}
          />
          <FieldError>{fieldState.error?.message}</FieldError>
        </Field>
      )}
    />
  )
  const email = (
    <Controller
      name="email"
      control={control}
      render={({ field, fieldState }) => (
        <Field data-fill-field={FillFieldKey.OWNER_EMAIL}>
          <Label htmlFor={id('email')}>Email</Label>
          <Input
            {...field}
            id={id('email')}
            type="email"
            aria-invalid={!!(fieldState.error || emailCheck) || undefined}
          />
          <FieldError>{fieldState.error?.message ?? emailCheck}</FieldError>
        </Field>
      )}
    />
  )
  const dob = (
    <Controller
      name="dob"
      control={control}
      render={({ field, fieldState }) => (
        <Field data-fill-field={FillFieldKey.OWNER_DOB}>
          <Label htmlFor={id('dob')}>Date of birth</Label>
          <DOBInput
            value={field.value ?? undefined}
            onChange={(date) => field.onChange(date ?? null)}
            inputProps={{ id: id('dob'), 'aria-invalid': !!(fieldState.error || dobCheck) || undefined }}
          />
          <FieldError>{fieldState.error?.message ?? dobCheck}</FieldError>
        </Field>
      )}
    />
  )
  const mobile = (
    <Controller
      name="mobile"
      control={control}
      rules={{ validate: (value) => validatePhone(value) ?? true }}
      render={({ field, fieldState }) => (
        <Field>
          <FieldLabel htmlFor={id('mobile')} optional>
            Phone number
          </FieldLabel>
          <PhoneInput
            id={id('mobile')}
            value={field.value}
            onChange={field.onChange}
            error={!!fieldState.error}
          />
          <FieldError>{fieldState.error?.message}</FieldError>
        </Field>
      )}
    />
  )
  const maritalStatus = (
    <Controller
      name="maritalStatus"
      control={control}
      render={({ field }) => (
        <Field data-fill-field={FillFieldKey.OWNER_MARITAL_STATUS}>
          <Label htmlFor={id('marital-status')}>Marital status</Label>
          <Select value={field.value} onValueChange={field.onChange}>
            <SelectTrigger
              id={id('marital-status')}
              className="w-full"
              aria-invalid={!!maritalStatusCheck || undefined}
            >
              <SelectValue placeholder="Select marital status" />
            </SelectTrigger>
            <SelectContent>
              {MARITAL_STATUS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError>{maritalStatusCheck}</FieldError>
        </Field>
      )}
    />
  )

  if (columns === 2) {
    return (
      <FieldGroup className="grid gap-x-4 md:grid-cols-2">
        <div data-fill-field={FillFieldKey.OWNER_NAME}>{firstName}</div>
        {lastName}
        {email}
        {mobile}
        {dob}
        {maritalStatus}
      </FieldGroup>
    )
  }

  return (
    <FieldGroup>
      <div className="grid grid-cols-2 gap-4" data-fill-field={FillFieldKey.OWNER_NAME}>
        {firstName}
        {lastName}
      </div>
      {email}
      {dob}
      {mobile}
      {maritalStatus}
    </FieldGroup>
  )
}
